/**
 * Display formatting, defined once.
 *
 * One date format for the whole app — "07/10/2026, 4:30 PM" (DD/MM/YYYY), in
 * IST — so that a timestamp never reads differently from one screen to the
 * next. Product decision 2026-10-08, replacing "12 Aug 2026"; it also matches
 * the date-picker boxes on a DD/MM/YYYY computer. Everything here is presentation only; the API deals in
 * epoch milliseconds throughout.
 */

/**
 * The business runs in one timezone, and the server writes epoch ms. Rendering
 * in the viewer's local zone would show a Bengaluru operator's 6am task at 1:30
 * the previous morning if they happened to open it from another country.
 */
const IST = "Asia/Kolkata";

/** en-GB writes exactly DD/MM/YYYY. */
const DATE_PARTS = new Intl.DateTimeFormat("en-GB", {
  timeZone: IST,
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

const TIME_ONLY = new Intl.DateTimeFormat("en-IN", {
  timeZone: IST,
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

/** "07/10/2026, 4:30 PM" — the app-wide format. */
export function formatDateTime(epochMs: number | null | undefined): string {
  if (!epochMs) return "—";
  return `${DATE_PARTS.format(new Date(epochMs))}, ${formatTime(epochMs)}`;
}

/** "07/10/2026" — the day of a moment, in IST. */
export function formatDate(epochMs: number | null | undefined): string {
  if (!epochMs) return "—";
  return DATE_PARTS.format(new Date(epochMs));
}

/** "4:30 PM" — for a window, where the date is already obvious from context. */
export function formatTime(epochMs: number | null | undefined): string {
  if (!epochMs) return "—";
  return TIME_ONLY.format(new Date(epochMs)).replace(/\b(am|pm)\b/i, (match) =>
    match.toUpperCase(),
  );
}

/**
 * "Today, 4:30 PM" / "Tomorrow, 7:30 AM" — a moment in the next day or so,
 * where the day word is plainer than a date. Anything further out gets the
 * full "07/10/2026, 4:30 PM".
 */
export function formatSoon(
  epochMs: number | null | undefined,
  now: number = Date.now(),
): string {
  if (!epochMs) return "—";
  const day = DATE_PARTS.format(new Date(epochMs));
  if (day === DATE_PARTS.format(new Date(now))) return `Today, ${formatTime(epochMs)}`;
  if (day === DATE_PARTS.format(new Date(now + 86_400_000))) {
    return `Tomorrow, ${formatTime(epochMs)}`;
  }
  return formatDateTime(epochMs);
}

/**
 * "in 3h" / "2d ago". **Secondary text only** — the design system forbids
 * relative time as the sole form, because "2h ago" cannot be checked against
 * anything and is ambiguous across a shift change.
 */
export function formatRelative(
  epochMs: number | null | undefined,
  now: number = Date.now(),
): string {
  if (!epochMs) return "";

  const deltaMs = epochMs - now;
  const past = deltaMs < 0;
  const minutes = Math.round(Math.abs(deltaMs) / 60_000);

  if (minutes < 1) return "now";

  const [value, unit] =
    minutes < 60
      ? [minutes, "m"]
      : minutes < 60 * 24
        ? [Math.round(minutes / 60), "h"]
        : [Math.round(minutes / (60 * 24)), "d"];

  return past ? `${value}${unit} ago` : `in ${value}${unit}`;
}

/** True when a due date has passed — drives the overdue emphasis in the inbox. */
export function isOverdue(
  dueAt: number | null | undefined,
  now: number = Date.now(),
): boolean {
  return Boolean(dueAt) && (dueAt as number) < now;
}

/** Sentence-cases a machine value like `in_progress` for display. */
export function humanise(value: string): string {
  const spaced = value.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Epoch ms from the "YYYY-MM-DD HH:mm:ss" IST strings the call log stores, so
 * call times go through the same formatter as everything else. Null for
 * anything that does not parse.
 */
export function parseIstDateTime(value: string | null | undefined): number | null {
  if (!value) return null;
  const match = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})$/.exec(value.trim());
  if (!match) return null;
  const epoch = Date.parse(`${match[1]}T${match[2]}+05:30`);
  return Number.isNaN(epoch) ? null : epoch;
}

/** "YYYY-MM-DD" for a moment, in IST — what date inputs and the API expect. */
export function istDateString(epochMs: number = Date.now()): string {
  // en-CA formats as YYYY-MM-DD.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: IST,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(epochMs));
}

/** "1:39", "12:05", "1:02:10" — exact, unlike "a few seconds". */
export function formatDuration(totalSeconds: number | null | undefined): string {
  const seconds = Math.max(0, Math.floor(totalSeconds ?? 0));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = String(seconds % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
}

/**
 * "3h 12m", "45m", "40s" — a total of time for a summary, where seconds would
 * be noise. Exact per-call durations use formatDuration instead.
 */
export function formatHoursMinutes(totalSeconds: number | null | undefined): string {
  const seconds = Math.max(0, Math.floor(totalSeconds ?? 0));
  if (seconds < 60) return `${seconds}s`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/** "2d 4h", "3h 10m", "25m" — how long something has been waiting. */
export function formatWaiting(totalSeconds: number | null | undefined): string {
  const seconds = Math.max(0, Math.floor(totalSeconds ?? 0));
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3_600);
  if (days > 0) return hours ? `${days}d ${hours}h` : `${days}d`;
  return formatHoursMinutes(seconds);
}

/** "9876543210" → "98765 43210". Anything else is returned unchanged. */
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return "—";
  return /^\d{10}$/.test(phone) ? `${phone.slice(0, 5)} ${phone.slice(5)}` : phone;
}

/** "09:30" / "18:05" → "9:30 AM" / "6:05 PM" — a wall-clock time of day. */
export function formatClockTime(value: string | null | undefined): string {
  const match = /^(\d{2}):(\d{2})/.exec(value ?? "");
  if (!match) return value || "—";
  const hours = Number(match[1]);
  const suffix = hours >= 12 ? "PM" : "AM";
  return `${hours % 12 === 0 ? 12 : hours % 12}:${match[2]} ${suffix}`;
}

/** "YYYY-MM-DD" → "07/10/2026" — a calendar date, so no timezone shift. */
export function formatDateOnly(value: string | null | undefined): string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return value || "—";
  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
}

/**
 * How much of a timestamp a list needs, given what it already shows:
 *  - "time" — the list covers one day, so "4:30 PM" is enough;
 *  - "dayTime" / "full" — the app-wide "07/10/2026, 4:30 PM". A date is
 *    always written whole (decision 2026-10-08: DD/MM/YYYY everywhere), so
 *    the two are the same; "dayTime" stays for the callers that ask for it.
 */
export type TimestampDetail = "time" | "dayTime" | "full";

export function formatCompactDateTime(
  epochMs: number | null | undefined,
  detail: TimestampDetail,
): string {
  if (!epochMs) return "—";
  if (detail === "time") return formatTime(epochMs);
  return formatDateTime(epochMs);
}

const RUPEES = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });

/** "₹1,24,500" — rupees, whole, in Indian digit grouping. */
export function formatRupees(amount: number | null | undefined): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return "—";
  const rounded = Math.round(amount);
  return `${rounded < 0 ? "-" : ""}₹${RUPEES.format(Math.abs(rounded))}`;
}

/** "1,245" — a count in Indian grouping, rounded (averages can be fractional). */
export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return RUPEES.format(Math.round(value));
}

const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKDAY_LONG = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

/**
 * The weekday of a "YYYY-MM-DD" calendar date — computed in UTC on the bare
 * date, so the viewer's timezone can never move it to the neighbouring day.
 */
export function weekdayOf(value: string, form: "short" | "long" = "short"): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return "";
  const index = new Date(`${value}T00:00:00Z`).getUTCDay();
  return (form === "long" ? WEEKDAY_LONG : WEEKDAY_SHORT)[index] ?? "";
}

/** "Sun 11/10/2026" — a calendar date with its weekday in front. */
export function formatDateWithWeekday(value: string | null | undefined): string {
  if (!value) return "—";
  const day = weekdayOf(value);
  return day ? `${day} ${formatDateOnly(value)}` : formatDateOnly(value);
}
